/**
 * Who may write, expressed as a pure function so it can be checked directly.
 *
 * A guard against accidents, NOT an authorization boundary -- the server's
 * permission checks are the real control, and anyone determined simply flips
 * the switch. The point is that it cannot happen by accident:
 *
 * An administrator holds every permission, and because ADMIN_EMAIL_DOMAINS
 * grants bootstrap admin to a whole email domain, that is most colleagues. The
 * table is full of controls that commit on a single click (stage select,
 * JA/NEJ, star rating), so someone looking a company up can change a seller's
 * live pipeline by hitting the wrong row. That is a slip, not a decision.
 */
export interface WriteAccessInput {
  /** Whether this viewer works a queue of their own. null while unknown. */
  isSeller: boolean | null
  /** Holds manage_leads. With isSeller this is a team lead. */
  managesLeads: boolean
  /** Whose queue is on screen; null means the viewer's own. */
  queueOwner: string | null
  /** Has editing been switched on. */
  armed: boolean
  /** Which queue it was switched on for. */
  armedFor: string | null
}

export interface WriteAccessDecision {
  canEdit: boolean
  /** Whether this viewer could be offered the switch at all. */
  canToggle: boolean
}

export function decideWriteAccess(
  { isSeller, managesLeads, queueOwner, armed, armedFor }: WriteAccessInput,
): WriteAccessDecision {
  // Arming counts only in the queue it was armed in. Editing someone else's
  // leads is a separate decision from editing your own, so crossing between
  // queues drops it -- and comparing rather than clearing on transition means
  // there is no moment where stale arming still counts.
  const editing = armed && armedFor === queueOwner

  // Viewer unknown: no edits. Guessing the other way would let a stray click
  // through in the one moment we cannot check.
  if (isSeller === null) return { canEdit: false, canToggle: false }

  // A team lead works a queue and manages everyone's leads, so editing any
  // queue is the job: no switch, every queue, Tabell included. manage_leads
  // without a queue (an admin looking things up) gets no such pass.
  if (isSeller && managesLeads) return { canEdit: true, canToggle: false }

  // Someone else's queue is locked until deliberately unlocked, even for a
  // seller: being a seller earns you edit rights over YOUR queue, not another
  // person's. This is the case the seller short-circuit below would otherwise
  // wave straight through.
  if (queueOwner) return { canEdit: editing, canToggle: true }

  // Sellers are never gated on their own queue. Working leads is the job.
  if (isSeller) return { canEdit: true, canToggle: false }

  return { canEdit: editing, canToggle: true }
}

/** Tooltip on every write control while read-only mode disables it. */
export const READ_ONLY_TITLE = 'Skrivskyddat läge. Välj Aktivera redigering överst för att ändra.'

export interface WriteControlProps {
  disabled: boolean
  title: string | undefined
}

/**
 * disabled and title for a control that writes, so read-only mode greys it out
 * for real instead of leaving it clickable and dropping the write. `title` is
 * the control's normal tooltip; read-only replaces it with the reason.
 */
export function writeControlProps(canEdit: boolean, title?: string): WriteControlProps {
  return canEdit
    ? { disabled: false, title }
    : { disabled: true, title: READ_ONLY_TITLE }
}
